/**
 * API service for assessment tool
 * Makes HTTP requests to Cloudflare Functions for database operations
 */
import { User, Assessment, Response, Result } from '../models';
import { Category } from '../models/Question';
import questions from '../data/questions';

// Mapping from category slugs (from questions.ts) to full names (expected by backend)
const categorySlugToFullName: Record<string, string> = {
  'strategy-alignment': 'Strategy & Alignment',
  'people': 'People',
  'portfolio-reporting': 'Portfolio Reporting',
  'scalability-growth': 'Scalability & Growth',
  'tech-tools': 'Tech & Tools',
  'value-focus': 'Value Focus',
  'change-transformation': 'Change & Transformation',
  'process-governance': 'Process & Governance'
};

// API base URL - use relative path to work in all environments
const API_BASE_URL = '/api';

/**
 * Create a new user record
 * 
 * @param userData User data to create
 * @returns Created user with ID
 */
export const createUser = async (userData: Omit<User, 'id'>): Promise<User> => {
  try {
    const response = await fetch(`${API_BASE_URL}/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Failed to create user: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

/**
 * Create a new assessment
 * 
 * @param userId User ID to associate with the assessment
 * @returns Created assessment with ID
 */
export const createAssessment = async (userId: string): Promise<Assessment> => {
  try {
    const response = await fetch(`${API_BASE_URL}/assessments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Failed to create assessment: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error creating assessment:', error);
    throw error;
  }
};

/**
 * Save assessment responses
 * 
 * @param assessmentId Assessment ID
 * @param responses Response data to save
 * @returns Saved responses
 */
export const saveResponses = async (
  assessmentId: string, 
  responses: Record<string, number>
): Promise<Response[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/responses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ assessmentId, responses }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Failed to save responses: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error saving responses:', error);
    throw error;
  }
};

/**
 * Save assessment results
 * 
 * @param assessmentId Assessment ID
 * @param responses Response data to calculate results from
 * @returns Saved assessment with results
 */
export const saveResults = async (
  assessmentId: string,
  userId: string, // Added userId parameter
  responses: Record<string, number>
): Promise<any> => {
  try {
    // Calculate results by category using full names
    const resultsByFullName: Record<string, { total: number; count: number }> = {};
    const questionMap = new Map(questions.map(q => [q.id, q])); // Create a map for quick lookup

    for (const [questionId, value] of Object.entries(responses)) { // questionId is 'q1', 'q2', etc.
      const question = questionMap.get(questionId);
      if (!question) {
        console.warn(`Question with ID ${questionId} not found in questions data. Skipping.`);
        continue; 
      }
      
      const categorySlug = question.category; // e.g., 'strategy-alignment'
      const fullCategoryName = categorySlugToFullName[categorySlug];
      
      if (!fullCategoryName) {
        console.warn(`Full category name for slug ${categorySlug} (from question ${questionId}) not found in mapping. Skipping.`);
        continue;
      }

      if (!resultsByFullName[fullCategoryName]) {
        resultsByFullName[fullCategoryName] = { total: 0, count: 0 };
      }
      resultsByFullName[fullCategoryName].total += value;
      resultsByFullName[fullCategoryName].count += 1;
    }
    
    // Calculate total score for each category (now using full names as keys)
    const categoryResults: Record<string, number> = {};
    for (const [fullCategoryName, data] of Object.entries(resultsByFullName)) {
      categoryResults[fullCategoryName] = data.total;
    }
    
    // Send to API
    const response = await fetch(`${API_BASE_URL}/results`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        assessmentId,
        userId, // Added userId to payload
        results: Object.entries(categoryResults).map(([categoryName, score]) => ({ // Transformed results to array
          categoryName,
          score
        }))
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Failed to save results: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error saving results:', error);
    throw error;
  }
};

/**
 * Get assessment by ID
 * 
 * @param assessmentId Assessment ID
 * @returns Assessment data with user and responses
 */
export const getAssessment = async (assessmentId: string): Promise<Assessment | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/assessments/${assessmentId}`);
    
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      // Attempt to get error text, but be cautious as it might not be JSON
      const errorText = await response.text();
      console.error('Error response text from /api/assessments/:id (in getAssessment):', errorText);
      let parsedErrorData = { error: `Server returned ${response.status} for assessment ID ${assessmentId}` }; // Default error
      try {
        parsedErrorData = JSON.parse(errorText); // Try to parse if it's JSON
      } catch (e) {
        // If not JSON, use the text as part of the error or a generic message
        parsedErrorData.error = errorText.substring(0, 200) || `Failed to get assessment: ${response.statusText}`;
      }
      throw new Error(parsedErrorData.error || `Failed to get assessment: ${response.statusText}`);
    }
    
    // For successful responses, get the raw text first to inspect it
    const responseText = await response.text();
    console.log('Raw response text from /api/assessments/:id (in getAssessment success path):', responseText);
    try {
      // Manually parse the text after logging it
      return JSON.parse(responseText);
    } catch (e) {
      console.error(`Failed to parse responseText as JSON (in getAssessment success path for ID ${assessmentId}):`, e);
      throw new Error(`Failed to parse server response for assessment ID ${assessmentId}: ${e.message}. Response text: ${responseText.substring(0, 500)}`);
    }
  } catch (error) {
    console.error('Error getting assessment:', error);
    throw error;
  }
};

/**
 * Get assessment results by assessment ID
 * 
 * @param assessmentId Assessment ID
 * @returns Assessment results with category scores
 */
export const getResults = async (assessmentId: string): Promise<any | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/assessments/${assessmentId}/results`);
    
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      const errorData = await response.json();
      throw new Error(errorData.error || `Failed to get results: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting results:', error);
    throw error;
  }
};

/**
 * Get all assessments
 * 
 * @returns Array of all assessments
 */
export const getAssessments = async (): Promise<Assessment[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/assessments`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Failed to get assessments: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting assessments:', error);
    throw error;
  }
};
